import { useState, type ReactNode } from "react";
import type { PoiCategory } from "../api/client";
import { fr } from "../i18n/fr";
import { courseUpcoming } from "./layers";
import { swatchStyle, type Styles } from "./symbology";
import type { LayerStyle } from "./symbology";

export interface LegendEntry { key: string; label: string; swatch: ReactNode }
/** one layer of the map: its visibility box and, when it is shown, the meaning of its symbols */
export interface LegendGroup { key: string; label: string; visible: boolean; entries: LegendEntry[] }

const DETAILS_KEY = "secoursnav.legendDetails";

export const lineSwatch = (s: LayerStyle) => <span className="legend-line" style={swatchStyle(s)} />;
export const dotSwatch = (color: string, text = "") => <span className="dot-swatch" style={{ background: color }}>{text}</span>;

/** symbols of the network effects (web spec §7.4) */
export function effectEntries(S: Styles): LegendEntry[] {
  const entries = ["CLOSED_ALL", "PUBLIC_CLOSED", "ENCLOSED", "INACCESSIBLE", "EMERGENCY_ACCESS", "ONEWAY_LIFTED", "ONEWAY_REVERSED", "COURSE", "SOFT_OBSTACLE"]
    .map((k) => ({ key: k, label: fr.legend[k], swatch: lineSwatch(S[k]) }));
  entries.splice(entries.findIndex((e) => e.key === "COURSE") + 1, 0,
    { key: "COURSE_UPCOMING", label: fr.legend.COURSE_UPCOMING, swatch: lineSwatch(courseUpcoming(S.COURSE)) });
  return entries;
}

export function barrierEntries(S: Styles): LegendEntry[] {
  return [
    { key: "nc", label: fr.legend.barrierNonCrossable, swatch: dotSwatch(S.BARRIER_NON_CROSSABLE.color, "×") },
    { key: "c", label: fr.legend.barrierCrossable, swatch: dotSwatch(S.BARRIER_CROSSABLE.color, "+") },
    { key: "cp", label: fr.legend.CROSSING_POINT, swatch: dotSwatch(S.COURSE.color) },
  ];
}

/** one entry per POI category of the event, in its colour (C1) */
export function categoryEntries(categories: PoiCategory[] | undefined, fallback: string): LegendEntry[] {
  const list = [...(categories ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  if (list.length === 0) return [{ key: "poi", label: fr.legend.pois, swatch: dotSwatch(fallback) }];
  return list.map((c) => ({ key: c.id, label: c.name + (c.hidden ? ` (${fr.legend.hiddenInVehicles})` : ""), swatch: dotSwatch(c.color || fallback) }));
}

/**
 * Legend of a map (C1): the layers with their visibility box, always shown, and on demand the symbols of each
 * shown layer. A hidden layer shows no symbol. The symbols are folded by default so that the legend does not
 * cover the map, the choice of the operator is remembered by the browser.
 */
export function MapLegend({ groups, onToggle }: { groups: LegendGroup[]; onToggle(key: string, visible: boolean): void }) {
  const [details, setDetails] = useState(() => localStorage.getItem(DETAILS_KEY) === "1");
  const toggle = () => { const n = !details; setDetails(n); localStorage.setItem(DETAILS_KEY, n ? "1" : "0"); };
  return (
    <div className={"legend" + (details ? " detailed" : "")} role="group" aria-label={fr.legend.title}>
      <button type="button" className="legend-head" aria-expanded={details} onClick={toggle} title={details ? fr.legend.hideSymbols : fr.legend.showSymbols}>
        <strong>{fr.legend.title}</strong> <span aria-hidden="true">{details ? "▾" : "▸"}</span>
      </button>
      {groups.map((g) => (
        <div key={g.key} className="legend-group">
          <label className="check"><input type="checkbox" checked={g.visible} onChange={(e) => onToggle(g.key, e.target.checked)} /> <span className="legend-group-label">{g.label}</span></label>
          {details && g.visible && g.entries.map((e) => <div key={e.key} className="legend-item">{e.swatch} {e.label}</div>)}
        </div>
      ))}
    </div>
  );
}
